import { mapActions, mapGetters } from 'vuex';

import i18n from './i18n';
import form from './form';
import moduleStore from './module';
import settings from './settings';
import hrModule from './hrModule';
import userModule from './user';
import tasksModule from './tasks';
import dropdownModule from './dropdown';
import notifications from './notifications';
import attachmentsModule from './attachments';
import absenceModule from './absence';
import modals from './modals';

const modules = [
  i18n,
  form,
  moduleStore,
  settings,
  hrModule,
  userModule,
  tasksModule,
  notifications,
  modals,
  dropdownModule,
  attachmentsModule,
  absenceModule,
] as const;

type UnionToIntersection<U> =
  (U extends any ? (k: U) => void : never) extends
  (k: infer I) => void ? I : never;

type MapGetters<T> = {
  [K in keyof T]: T[K] extends (state: any, ...args: any[]) => infer R ? (() => R) : never;
};
type MapActions<T> = {
  [K in keyof T]: T[K] extends (context: any, ...args: infer A) => infer R ? (...args: A) => R : never;
};

type ModuleGetters<M> = M extends { getters: infer G } ? G : never;
type ModuleActions<M> = M extends { actions: infer A } ? A : never;

type AllGetters<Modules extends readonly any[]> = UnionToIntersection<
  MapGetters<ModuleGetters<Modules[number]>>
>;
type AllActions<Modules extends readonly any[]> = UnionToIntersection<
  MapActions<ModuleActions<Modules[number]>>
>;

type Getters = AllGetters<typeof modules>;
type Actions = AllActions<typeof modules>;

/** alias for vuex mapGetters that uses types from the store */
export function typedMapGetters<R extends { [alias: string]: keyof Getters }>(mapping: R) {
  return mapGetters(mapping as Record<string, string>) as {
    readonly [K in keyof R]: Getters[R[K]];
  };
}

/** alias for vuex mapActions that uses types from the store */
export function typedMapActions<R extends(keyof Actions)[]>(actions: R) {
  return mapActions(actions) as {
    [K in R[number]]: Actions[K];
  };
}
